import { api } from '@ddc-webplatform/cmsc-modules';
import { LOCALE } from 'constants/composer';
import type {
	AvailablePagesData,
	BulkDeleteResponse,
	BulkUpdateEntry,
	BulkUpdateResponse,
	HtmEntry,
	LocalePath,
	PageData,
	SitemapCollectionData,
	SitemapEntry,
	SitemapEntryData,
	SitemapTreeData,
	UpdateSitemapEntryPayload,
	XmlEntry
} from 'models';

const sitemapModule = api.sitemap;

export const SitemapApi = {
	loadSitemapTree: (locale: string): Promise<SitemapTreeData> =>
		sitemapModule.loadSitemapTree(locale),

	loadSitemapEntry: async (path: string): Promise<SitemapEntry | null> => {
		const data: SitemapEntryData = await sitemapModule.loadSitemapEntries(path);
		return data?.CONTROL?.[0] ?? null;
	},

	loadSitemapCollectionData: (
		pageAlias: string
	): Promise<SitemapCollectionData> =>
		sitemapModule.loadSitemapCollectionData(pageAlias),

	loadAvailablePages: (): Promise<AvailablePagesData> =>
		sitemapModule.loadAvailablePages(),

	validateSinglePagePath: async (pagePath: string): Promise<boolean> => {
		try {
			await sitemapModule.validatePagePath(pagePath);
			return true;
		} catch (err: unknown) {
			const { status } = err as { status?: number };
			if (status === 406) return false;
			throw err;
		}
	},

	validateMultiplePagePaths: (
		pagePaths: LocalePath[]
	): Promise<Record<string, boolean>> => {
		const pathsByLocale = Object.fromEntries(
			pagePaths.map(({ locale, path }) => [locale, path])
		);
		return sitemapModule.validateMultiplePagePaths(pathsByLocale);
	},

	bulkDelete: (pageAliases: string[]): Promise<BulkDeleteResponse> =>
		sitemapModule.bulkDelete(pageAliases),

	bulkUpdate: (entries: BulkUpdateEntry[]): Promise<BulkUpdateResponse> =>
		sitemapModule.bulkUpdate(entries),

	updateSitemapEntry: (data: PageData): Promise<SitemapEntry> => {
		const xmlEntry: XmlEntry | null = data.showInXmlSitemap
			? ({
					startDate: data.addToSitemapOn || null,
					endDate: data.removeFromSitemapOn || null
			  } as XmlEntry)
			: null;
		const htmEntry: HtmEntry | null = data.showInHtmlSitemap
			? { pageAlias: data.pageAlias!, label: data.label ?? '', target: '_self' }
			: null;
		const localeData = Object.fromEntries(
			(data.localePaths ?? []).map(({ locale, path }: LocalePath) => [
				locale,
				{ title: data.label ?? '', path }
			])
		);
		const payload: UpdateSitemapEntryPayload = {
			primaryLocale: LOCALE,
			localeData,
			xmlEntry,
			htmEntry,
			noIndex: data.noIndex ?? false,
			noFollow: data.noFollow ?? false
		};
		return sitemapModule.patchSitemapEntry(
			data.pageAlias!,
			data.pageId!,
			payload
		);
	}
};
